import * as types from '../mutation-types'
import { initializeProactiveRefresh, clearRefreshTimer } from '../../api.js' // Import timer functions
import { unregisterFcmToken } from '../../utils/firebase-messaging'
/*
 * #2103 — the chat Firebase identity has to go with the CS3 one.
 *
 * A LEAF module with no CS3 imports, deliberately: `chatService` imports
 * `./api`, and `api.js` imports this store, so importing `chatService` here
 * would close a cycle and hand one of them a half-initialised module in a
 * production build.
 */
import { forgetChatIdentity } from '../../chatSignOut.js'

const state = {
    main: {
        id: 0,
        userData: [],
        role: 0,
        roleSlug: '',
        // #1747 — the employer's access level for the CLIENT this session is on:
        // 'view_access' | 'create_edit' | 'api_access' | 'no_access'.
        // Supplied by the login payload so no component re-queries it. A NULL
        // status arrives as 'no_access' (#2084: blank = switched off).
        //
        // The DEFAULT is no_access too, for a different reason: it applies
        // before the login payload has arrived, where an unknown grant is not a
        // grant.
        employerAccess: 'no_access',
        // #1470 — canonicalRole is the user's TOP-LEVEL role (users.role_id),
        // distinct from `role` which is their effective per-tenant role for
        // the currently-selected client. SAs send canonicalRole in the
        // cs3-context-role header so server-side logging + ALB routing can
        // tell "real SA traffic" apart from "SA impersonating CA in tenant X".
        // Set on login from data.user.role_id; UNTOUCHED by tenant switches.
        canonicalRole: '',
        token: 0,
        isLoginSuper: false,
        actingSuper: null,
        clientId: '', //'bb7fddf0-6a0f-11e7-b301-3f66590aa632',
        clientUserId: '',
        clientName: '',
        userName: '',
        firstName: '',
        lastName: '',
        userFullName: '',
        employeeId: '',
        loggedInEmployeeId: '',
        userImage: '',
        userPhone: '',
        actionLogType: '',
        editEmployeesFrom: '',
        signInSheetConfig: {},
        signInPageBrokenStatus: '',
        userNotificationType: '',
        wishListType: '',
        jobFilter: {
            parent: 'Alljob',
            child: 'day'
        },
        subdomain: '',
        companyId: '',
        employerUserId: '',
        getOpenJob: 0,
        currency: 'usd',
        email: '',
        gcs_signin_sheet_template: '',
        edit_user_tab: '',
        user_language: '',
        nylas_user_email: '',
        token_expiry: '',
        is_billing_employee: false,
        query_param_client_id: '',
        query_param_app_source: false,
        hasPendingBatch: false
    }

}
const getters = {
    userID: state => state.main.id,
    userToken: state => state.main.token,
    userRole: state => state.main.role,
    // #1470 — getter used by api.js axios interceptor + per-component header
    // injection sites to populate cs3-context-role. Falls back to per-tenant
    // role for pre-#1470 sessions where canonicalRole was never set.
    userCanonicalRole: state => state.main.canonicalRole || state.main.role,
    userName: state => state.main.userName,
    firstName: state => state.main.firstName,
    employeeId: state => state.main.employeeId,
    loggedInEmployeeId: state => state.main.loggedInEmployeeId,
    userRoleSlug: state => state.main.roleSlug,
    employerAccess: state => state.main.employerAccess,
    userLanguage: state => state.main.user_language,
    edit_user_tab: state => state.main.edit_user_tab,
    getNylasUserEmail: state => state.main.nylas_user_email,
    getTokenExpiry: state => state.main.token_expiry,
    isBillingEmployee: state => state.main.is_billing_employee,
    actingSuper: state => state.main.actingSuper,
    query_param_client_id: state => state.main.query_param_client_id,
    query_param_app_source: state => state.main.query_param_app_source,
    hasPendingBatch: state => state.main.hasPendingBatch
}
const actions = {
    userSession({ commit, state }, data) {
        commit(types.USER_DATA_CHANGE, data)
    },
    setEmployeeId({commit},id) {
        commit(types.SET_EMPLOYEE_ID, id)
    },
    onSwitchClient({ commit, state }, data) {
        // console.log('action', data);
        commit(types.SET_SWITCH_CLIENT, data);
    },
    // #1747 — set the employer access level on its own, for the client-switch
    // path: that response is not login-shaped, so USER_DATA_CHANGE cannot carry it.
    setEmployerAccess({ commit }, data) {
        commit(types.SET_EMPLOYER_ACCESS, data);
    },
    setActionTypeForLog({ commit, state }, data) {
        commit(types.SET_ACTION_FOR_LOG, data);
    },
    setEditEmployeesFrom({ commit, state }, data) {
        commit(types.SET_EDIT_EMPLOYEE_FROM, data);
    },
    setClientdata({ commit, state }, data) {
        commit(types.SET_CLIENT_DATA, data);
    },
    setNotificationType({ commit, state }, data) {
        commit(types.SET_NOTYFICATION_TYPE, data);
    },
    setWishListType({ commit, state }, data) {
        commit(types.SET_WISH_LIST_TYPE, data);
    },
    setSelectedJobFilter({ commit, state }, data) {
        commit(types.SET_SELECTED_JOB_FILTER, data);
    },
    setLastOpenJob({ commit, state }, data) {
        commit('setLastOpenJob', data);
    },
    setUserImage({ commit, state }, data) {
        commit(types.SET_USER_IMAGE, data)
    },
    setLoginSuper({ commit, state }, data) {
        commit(types.IS_LOGIN_SUPER, data)
    },
    setActingSuper({ commit }, data) {
        commit(types.SET_ACTING_SUPER, data)
    },
    setHasPendingBatch({ commit }, value) {
        commit(types.SET_HAS_PENDING_BATCH, value)
    },
    setEditUserTab({ commit }, data) {
        commit(types.SET_EDIT_USER_TAB, data)
    },
    setNylasUserEmail({ commit }, email) {
        commit(types.SET_NYLAS_USER_EMAIL, email)
    },
    setTokenExpiry({ commit }, dateTime) {
        commit(types.SET_TOKEN_EXPIRY, dateTime)
    },
    
    // ENHANCED: setUserToken now starts proactive refresh timer
    setUserToken({ commit }, token) {
        commit(types.SET_USER_TOKEN, token)
        
        // Start proactive refresh timer when token is set (after successful login/refresh)
        if (token && token !== '') {
            console.log('🚀 Token set - starting proactive refresh timer');
            try {
                initializeProactiveRefresh();
            } catch (e) {
                console.error('Failed to initialize proactive refresh:', e);
            }
        }
    },

    // NEW: Logout action with timer cleanup
    logout({ commit, getters }) {
        console.log('🚪 Logging out - clearing refresh timer');

        // Clear the proactive refresh timer first
        try {
            clearRefreshTimer();
        } catch (e) {
            console.error('Failed to clear refresh timer:', e);
        }

        // Unregister FCM token before clearing auth
        try {
            var token = getters.userToken || '';
            unregisterFcmToken(token, '');
        } catch (e) {
            console.error('Failed to unregister FCM token:', e);
        }

        /*
         * #2103 — sign the chat Firebase app out and delete any persisted
         * identity an earlier build left behind.
         *
         * ⚠ WITHOUT THIS, LOGGING OUT OF CS3 LEFT CHAT SIGNED IN. The Firebase
         * user was written to IndexedDB by the default persistence and nothing
         * removed it, so the next person at a shared browser could read the
         * previous user's Firestore chat through devtools.
         *
         * Not awaited, and it cannot throw: a logout that fails because a chat
         * SDK would not load is a user stuck signed in. `location.reload` in
         * headerNav follows this, which is also what ends an in-memory
         * sign-in — this is for the identity that was already on disk.
         */
        try {
            forgetChatIdentity();
        } catch (e) {
            console.error('Failed to sign chat out:', e);
        }

        // Clear all user data
        commit(types.SET_USER_TOKEN, '');
        commit(types.SET_TOKEN_EXPIRY, '');
        commit(types.USER_DATA_CHANGE, {
            user: { id: 0 },
            role: { id: 0, slug: '' },
            token: ''
        });
        
        // Clear localStorage and cookies
        localStorage.removeItem('vuex');
        localStorage.clear();
        
        // Clear cookies
        document.cookie.split(";").forEach(function(c) { 
            document.cookie = c.replace(/^ +/, "").replace(/=.*/, "=;expires=" + new Date().toUTCString() + ";path=/"); 
        });
    },

    setIsBillingEmployee({commit}, status) {
        commit(types.SET_IS_BILLING_EMPLOYEE, status)
    },
    setQueryParamClientId({commit}, clientId) {
        commit(types.SET_QUERY_PARAM_CLIENT_ID, clientId)
    },
    setQueryParamAppSource({ commit }, app_source) {
        commit(types.SET_QUERY_PARAM_APP_SOURCE, app_source)
    }
}

const mutations = {
    [types.USER_DATA_CHANGE](state, data) {
        // console.log(data);
        state.main.userData = data;
        state.main.id = data.user.id;
        state.main.user_language = data.user.language;
        state.main.token = data.token;
        state.main.role = data.role.id;
        state.main.roleSlug = data.role.slug;
        // #1747 — carried on the same payload as role/permission. `||` not `??`:
        // an empty string from an older backend must also read as no_access.
        state.main.employerAccess = data.employer_access || 'no_access';
        // #1470 — canonical (top-level) role from users.role_id, exposed by
        // backend on the user payload as of MR for issue #1470. Falls back to
        // the per-tenant role for backward compatibility with pre-deploy
        // session refreshes that may not include the new field.
        state.main.canonicalRole = (data.user && data.user.role_id) || data.role.id;

        // console.log('USER_DATA_CHANGE 1');
        // console.log(data.user.language);
        if (data.role.slug == "client-admin" || data.role.slug == "employee" || data.role.slug == "power-user" || data.role.slug == "power-user-minus") {
            if (data.user) {
                state.main.userName = data.user.display_name;
                state.main.firstName = data.user.first_name;
                state.main.lastName = data.user.last_name;
                state.main.userFullName = data.user.first_name + " " + data.user.last_name;
                state.main.employeeId = data.user.employees[0].id;
                state.main.loggedInEmployeeId = data.user.employees[0].id;
                state.main.userImage = data.user.image;
                state.main.userPhone = data.user.phones;
                state.main.email = data.user.email;
                state.main.user_language = data.user.language
            }
            // console.log('USER_DATA_CHANGE 2');
            if (data.user && data.user.employees && data.user.employees.length && data.user.employees[0].client_employee && data.user.employees[0].client_employee.clients) {
                // console.log('USER_DATA_CHANGE 3');
                state.main.clientId = data.user.employees[0].client_employee.clients.id;
                state.main.clientName = data.user.employees[0].client_employee.clients.contact_person;
                state.main.clientUserId = data.user.employees[0].client_employee.clients.user_id;
                state.main.subdomain = data.user.employees[0].client_employee.clients.subdomain;
                state.main.currency = data.user.employees[0].client_employee.clients.currency;
            }
        } else if (data.role.slug == "employer-admin" || data.role.slug == "employer") {
            state.main.userName = data.user.display_name;
            state.main.firstName = data.user.first_name;
            state.main.lastName = data.user.last_name;
            state.main.userFullName = data.user.first_name + ' ' + data.user.last_name;
            state.main.userImage = data.user.image;
            state.main.userPhone = data.user.phones;
            state.main.email = data.user.email;
            state.main.user_language = data.user.language

            state.main.companyId = data.user.employer_users[0].employer.id;
            state.main.employerUserId = data.user.employer_users[0].id;

            state.main.clientId = '';
            state.main.clientName = '';
            state.main.clientUserId = '';
            state.main.subdomain = '';
            state.main.employeeId = '';
            state.main.loggedInEmployeeId = '';
            state.main.getOpenJob = 0;
        }
    },

    [types.SET_EMPLOYER_ACCESS](state, data) {
        state.main.employerAccess = data || 'no_access';
    },

    [types.SET_SWITCH_CLIENT](state, data) {
        state.main.userData = data;
        state.main.role = data.role.id;
        state.main.roleSlug = data.role.slug;
        // #1747 — carried on the same payload as role/permission. `||` not `??`:
        // an empty string from an older backend must also read as no_access.
        state.main.employerAccess = data.employer_access || 'no_access';
        // console.log(data);
        if (data.role.slug == "client-admin" || data.role.slug == "employee" || data.role.slug == "power-user" || data.role.slug == "power-user-minus" || data.role.slug == "auditor") {
            state.main.userName = data.user.display_name;
            state.main.firstName = data.user.first_name;
            state.main.lastName = data.user.last_name;
            state.main.userFullName = data.user.first_name + ", " + data.user.last_name;
            state.main.employeeId = data.user.employees[0].id;
            state.main.loggedInEmployeeId = data.user.employees[0].id;

            state.main.userImage = data.user.image;
            state.main.userPhone = data.user.phones;

            state.main.clientId = data.user.employees[0].client_employee.clients.id;
            state.main.clientName = data.user.employees[0].client_employee.clients.contact_person;
            state.main.clientUserId = data.user.employees[0].client_employee.clients.user_id;
            state.main.subdomain = data.user.employees[0].client_employee.clients.subdomain;
        }
    },

    [types.SET_EMPLOYEE_ID](state,id) {
        state.main.employeeId = id;
    },

    [types.SET_ACTION_FOR_LOG](state, data) {
        state.main.actionLogType = data;
    },

    [types.SET_EDIT_EMPLOYEE_FROM](state, data) {
        state.main.editEmployeesFrom = data;
    },

    [types.SET_CLIENT_DATA](state, data) {
        if (data && data.settings) {
            state.main.signInSheetConfig = {
                'sign-in-sheet': data.settings['sign-in-sheet'],
                'call-data-config': data.settings['call-data-config'],
                'job-info-config': data.settings['job-info-config'],
            };
            state.main.signInPageBrokenStatus = data.page_breakdown;
            state.main.gcs_signin_sheet_template = data.gcs_signin_sheet_template;
        }
    },

    [types.SET_NOTYFICATION_TYPE](state, data) {
        state.main.userNotificationType = data;
        //console.log(data);
    },
    [types.SET_WISH_LIST_TYPE](state, data) {
        state.main.wishListType = data;
        //console.log(data);
    },
    [types.SET_SELECTED_JOB_FILTER](state, data) {
        state.main.jobFilter = data;
        //console.log(data);
    },
    [types.SET_USER_IMAGE](state, data) {
        state.main.userImage = data;
    },

    [types.IS_LOGIN_SUPER](state, value) {
        state.main.isLoginSuper = value;
    },
    [types.SET_ACTING_SUPER](state, value) {
        state.main.actingSuper = value;
    },
    [types.SET_HAS_PENDING_BATCH](state, value) {
        state.main.hasPendingBatch = value;
    },
    [types.SET_EDIT_USER_TAB](state, value) {
        state.main.edit_user_tab = value;
    },

    setLastOpenJob(state, data) {
        state.main.getOpenJob = data;
        //console.log(data);
    },

    [types.SET_NYLAS_USER_EMAIL](state, email) {
        state.main.nylas_user_email = email;
    },

    [types.SET_TOKEN_EXPIRY](state,dateTime) {
        state.main.token_expiry = dateTime;
    },

    [types.SET_USER_TOKEN](state, token) {
        state.main.token = token;
    },

    [types.SET_IS_BILLING_EMPLOYEE](state,status) {
        state.main.is_billing_employee = status;
    },

    [types.SET_QUERY_PARAM_CLIENT_ID](state, client_id) {
        state.main.query_param_client_id = client_id;
    },
    [types.SET_QUERY_PARAM_APP_SOURCE](state, app_source) {
        state.main.query_param_app_source = app_source;
    },
 
    

}

/**
 * ⚠ #1898 — `getters` IS DELIBERATELY NOT EXPORTED, and that is load-bearing.
 *
 * This module defines a `userToken` getter returning the BARE token while
 * `store/getters.js` defines one returning `"Bearer " + token`. The module is
 * not namespaced, so exporting these would register `userToken` a second time
 * into the same flat namespace, overwrite the root one, and strip the scheme
 * from every request in the app at once.
 *
 * The getters above are therefore dead code kept for reference. Do not "tidy"
 * them into this export; delete them or namespace the module, and take the
 * decision deliberately.
 */
export default {
    state,
    actions,
    mutations
}
